/**
 * Horizontal Product Showcase — AARKA
 *
 * Products crossfade in place (no horizontal sliding).
 * Vertical scroll drives which product is visible.
 * Product image + text animate in/out with smooth transitions.
 * Uses GSAP ScrollTrigger for pinning, snapping, and scroll-driven updates.
 *
 * Scroll budget layout (e.g. 3 slides):
 *   [0.5vh dwell] [1.2vh slide-0→1] [1.2vh slide-1→2] [0.5vh dwell]
 */
if (!customElements.get('aarka-product-showcase')) {
  customElements.define(
    'aarka-product-showcase',
    class extends HTMLElement {
      connectedCallback() {
        if (window.AARKA && window.AARKA.ready) {
          this.init();
        } else {
          document.addEventListener('aarka:scroll-ready', () => this.init(), { once: true });
        }
      }

      disconnectedCallback() {
        if (this.st) this.st.kill();
        if (this.hintSt) this.hintSt.kill();
      }

      init() {
        var gsap = window.AARKA.gsap;
        var ScrollTrigger = window.AARKA.ScrollTrigger;

        this.wrapper = this.querySelector('.hps-wrapper');
        this.sticky = this.querySelector('.hps-sticky');
        this.counter = this.querySelector('.hps-counter');
        this.progressBar = this.querySelector('.hps-progress__bar');
        this.slides = Array.from(this.querySelectorAll('.hps-slide'));
        this.totalSlides = this.slides.length;
        this.currentSlide = -1;

        if (!this.wrapper || this.totalSlides === 0) return;

        var self = this;
        var vh = window.innerHeight;

        // ── Scroll budget ──
        var dwellVh = 0.5;            // 50vh dwell at start and end
        var scrollPerSlide = vh * 1.2; // 120vh per slide transition
        var transitions = this.totalSlides - 1;
        var totalScroll = (dwellVh * vh * 2) + (scrollPerSlide * transitions) + scrollPerSlide;

        this.wrapper.style.height = totalScroll + 'px';

        // ── Progress thresholds ──
        var dwellStartProgress = (dwellVh * vh) / totalScroll;
        var dwellEndProgress = 1 - ((dwellVh * vh) / totalScroll);
        var transitionRange = dwellEndProgress - dwellStartProgress;

        // ── Snap points — one per slide ──
        var snapPoints = [];
        for (var i = 0; i < this.totalSlides; i++) {
          if (this.totalSlides === 1) {
            snapPoints.push(0.5);
          } else {
            var slideProgress = dwellStartProgress + (i / (this.totalSlides - 1)) * transitionRange;
            snapPoints.push(slideProgress);
          }
        }

        this.st = ScrollTrigger.create({
          trigger: this.wrapper,
          start: 'top top',
          end: 'bottom bottom',
          pin: this.sticky,
          scrub: 0.4,
          snap: {
            snapTo: snapPoints,
            duration: { min: 0.2, max: 0.5 },
            delay: 0.05,
            ease: 'power1.inOut',
            inertia: false
          },
          onUpdate: function(st) {
            var progress = st.progress;
            var newIndex;

            // Before dwell-start: show slide 0
            if (progress <= dwellStartProgress) {
              newIndex = 0;
            }
            // After dwell-end: show last slide
            else if (progress >= dwellEndProgress) {
              newIndex = self.totalSlides - 1;
            }
            // In transition zone: map to slide index
            else {
              var localProgress = (progress - dwellStartProgress) / transitionRange;
              var exactSlide = localProgress * (self.totalSlides - 1);
              newIndex = Math.round(exactSlide);

              // Background blend uses exact position
              self.updateBackgroundBlend(exactSlide);
            }

            newIndex = Math.max(0, Math.min(newIndex, self.totalSlides - 1));

            if (newIndex !== self.currentSlide) {
              self.setActiveSlide(newIndex);
            }

            self.updateProgress(progress);
          }
        });

        // Activate first slide
        this.setActiveSlide(0);

        // Hide scroll hint after first scroll
        var hint = this.querySelector('.hps-scroll-hint');
        if (hint) {
          this.hintSt = ScrollTrigger.create({
            trigger: this.wrapper,
            start: 'top top-=100',
            onEnter: function() {
              hint.style.opacity = '0';
              hint.style.pointerEvents = 'none';
            }
          });
        }
      }

      setActiveSlide(index) {
        if (this.currentSlide >= 0 && this.currentSlide < this.totalSlides) {
          this.slides[this.currentSlide].classList.remove('hps-slide--active');
          this.slides[this.currentSlide].classList.add('hps-slide--exiting');

          var exitingSlide = this.slides[this.currentSlide];
          setTimeout(function () {
            exitingSlide.classList.remove('hps-slide--exiting');
          }, 400);
        }

        this.currentSlide = index;
        this.slides[index].classList.remove('hps-slide--exiting');
        this.slides[index].classList.add('hps-slide--active');

        this.updateCounter(index);
      }

      updateBackgroundBlend(exactSlide) {
        var currentIdx = Math.floor(exactSlide);
        var nextIdx = Math.min(currentIdx + 1, this.totalSlides - 1);
        var blend = exactSlide - currentIdx;

        // Get bg colors from the two slides
        var currentBg = this.slides[currentIdx].style.backgroundColor;
        var nextBg = this.slides[nextIdx].style.backgroundColor;

        if (currentBg && nextBg && currentIdx !== nextIdx) {
          this.sticky.style.backgroundColor = this.lerpColor(currentBg, nextBg, blend);
        } else if (currentBg) {
          this.sticky.style.backgroundColor = currentBg;
        }
      }

      lerpColor(color1, color2, t) {
        var c1 = this.parseColor(color1);
        var c2 = this.parseColor(color2);
        if (!c1 || !c2) return color1;

        var r = Math.round(c1[0] + (c2[0] - c1[0]) * t);
        var g = Math.round(c1[1] + (c2[1] - c1[1]) * t);
        var b = Math.round(c1[2] + (c2[2] - c1[2]) * t);
        return 'rgb(' + r + ',' + g + ',' + b + ')';
      }

      parseColor(str) {
        var match = str.match(/\d+/g);
        if (match && match.length >= 3) {
          return [parseInt(match[0]), parseInt(match[1]), parseInt(match[2])];
        }
        return null;
      }

      updateCounter(index) {
        if (!this.counter) return;
        var current = String(index + 1).padStart(2, '0');
        var total = String(this.totalSlides).padStart(2, '0');
        this.counter.textContent = current + ' / ' + total;
      }

      updateProgress(progress) {
        if (!this.progressBar) return;
        this.progressBar.style.transform = 'scaleX(' + progress + ')';
      }
    }
  );
}
